/**
 * DB timestamp helpers
 * --------------------
 * Several hot columns are `timestamp WITHOUT time zone` holding UTC
 * (gps_locations.timestamp, load_stops.estimated_arrival / actual_arrival /
 * actual_departure, loads.started_at / completed_at). PostgREST returns those
 * with NO offset ("2026-09-12T15:00:00"), and `new Date()` on such a string
 * parses it as LOCAL time. In Ontario that makes every naive timestamp read
 * 4-5 hours in the future: a 4-hour-old GPS ping showed as LIVE and every
 * "late vs plan" delta was off by the UTC offset (workflow audit S9).
 *
 * parseDbTime() treats an offset-less string as UTC; strings that already carry
 * Z / +hh:mm (timestamptz columns) are left alone.
 *
 * NOT for load_stops.time_window_start / _end: those are Toronto wall clock -- use
 * parseWindowTime / fmtWindow* below (__TZ_WINDOWS__).
 */

const HAS_OFFSET = /(Z|[+-]\d{2}:?\d{2})$/i;

export const parseDbTime = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  let s = String(value).trim();
  if (!HAS_OFFSET.test(s)) {
    // "2026-09-12 15:00:00.123456" -> "2026-09-12T15:00:00.123Z"
    s = s.replace(' ', 'T').replace(/(\.\d{3})\d+$/, '$1') + 'Z';
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

/** Whole minutes between now and a DB timestamp (positive = in the past). null if unparsable. */
export const minutesSince = (value, now = Date.now()) => {
  const d = parseDbTime(value);
  return d ? Math.round((now - d.getTime()) / 60000) : null;
};

/** Local-time display helpers that go through parseDbTime first. */
export const fmtDbDateTime = (value, opts) => {
  const d = parseDbTime(value);
  return d ? d.toLocaleString(undefined, opts) : '';
};
export const fmtDbTime = (value, opts) => {
  const d = parseDbTime(value);
  return d ? d.toLocaleTimeString(undefined, opts) : '';
};
export const fmtDbDate = (value, opts) => {
  const d = parseDbTime(value);
  return d ? d.toLocaleDateString(undefined, opts) : '';
};

/**
 * A calendar date string ('YYYY-MM-DD') as LOCAL midnight.
 *
 * `new Date('2026-09-12')` is UTC midnight by spec, so west of Greenwich it reads
 * back as the 11th: day buckets get labelled with the wrong day and a day-range
 * query starts hours early. Building the date from its parts keeps it local.
 */
export const localDateFromYMD = (ymd) => {
  if (!ymd) return null;
  // The WHOLE string must be a bare date. Matching only its first ten characters
  // would quietly swallow the time off a full timestamp and return midnight -- a
  // caller that passed one would get a plausible, wrong answer with no error.
  const m = String(ymd).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return parseDbTime(ymd);
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 0, 0, 0, 0);
};

/**
 * __TZ_PICKER__ (2026-09-23) a Date's LOCAL calendar day as 'YYYY-MM-DD'.
 * `d.toISOString().slice(0, 10)` is the UTC day -- tomorrow, every evening in Ontario.
 */
export const localYMD = (d = new Date()) => {
  const x = d instanceof Date ? d : parseDbTime(d);
  if (!x || isNaN(x.getTime())) return '';
  return x.getFullYear() + '-' + String(x.getMonth() + 1).padStart(2, '0') + '-' + String(x.getDate()).padStart(2, '0');
};

/**
 * __TZ_WINDOWS__ (2026-10-30) TIME WINDOWS ARE LOCAL WALL CLOCK, NOT UTC.
 * ---------------------------------------------------------------------
 * load_stops.time_window_start / time_window_end hold Toronto wall-clock time with no
 * offset ("2026-10-31T07:00:00" = 7:00 AM in Ontario). That is how the hub writes them
 * (Billing-Manager supabase_sync._win_iso, time_windows.iso_at, the Update Job path) and
 * how every other reader takes them: the hub's lists, Trello card text, Day graph,
 * availability, the Dispatch app, the DB flag load_needs_time_window, and this board's
 * own utils/timeWindow.js. The board used to read them with parseDbTime (naive = UTC),
 * so a 7:00-9:00 window showed as 3:00-5:00 AM (EDT; 2:00-4:00 after Nov 1) and the
 * Route Builder / ETA window checks were 4-5 hours off. And its picker saved UTC, which
 * the hub then showed 4-5 hours late.
 *
 * Every window read goes through parseWindowTime (-> a real instant, DST-safe, whatever
 * the browser's own zone) or the fmtWindow* helpers; the picker writes the same shape
 * the hub does. Do NOT run a window through parseDbTime. The OTHER naive columns
 * (estimated_arrival, actual_arrival, actual_departure, gps timestamps) really are UTC
 * and stay on parseDbTime. planned_arrival_at / planned_departure_at are timestamptz.
 */
export const WINDOW_TZ = 'America/Toronto';

let _wfmt = null;
const windowFmt = () => {
  if (!_wfmt) {
    _wfmt = new Intl.DateTimeFormat('en-US', {
      timeZone: WINDOW_TZ, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  }
  return _wfmt;
};

/** Toronto wall-clock parts of an instant: { y, mo, d, h, mi, s }. */
export const torontoParts = (ms) => {
  const o = {};
  windowFmt().formatToParts(new Date(ms)).forEach((p) => { o[p.type] = p.value; });
  return { y: Number(o.year), mo: Number(o.month), d: Number(o.day), h: Number(o.hour) % 24, mi: Number(o.minute), s: Number(o.second) };
};

/** Toronto's offset from UTC at an instant, in minutes (-240 EDT, -300 EST). */
const torontoOffsetMin = (ms) => {
  const p = torontoParts(ms);
  return Math.round((Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(ms / 1000) * 1000) / 60000);
};

/**
 * A Toronto wall-clock time -> the instant (Date). DST-safe: the offset is the one in
 * force at that wall time. 1:30 AM on the fall-back day (it happens twice) is the first,
 * daylight-time one; a spring-forward gap time (2:30 AM) lands an hour later.
 */
export const torontoWallToDate = (y, mo, d, h = 0, mi = 0, s = 0) => {
  const guess = Date.UTC(y, mo - 1, d, h, mi, s);
  const off1 = torontoOffsetMin(guess);
  let t = guess - off1 * 60000;
  const off2 = torontoOffsetMin(t);
  if (off2 !== off1) {
    const t2 = guess - off2 * 60000;
    // keep the candidate that really reads back as the wall time asked for
    const p = torontoParts(t2);
    if (p.h === h && p.mi === mi) t = t2;
  }
  return new Date(t);
};

const WALL = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{1,2}):(\d{2})(?::(\d{2})(?:\.\d+)?)?)?$/;

/** A stored time window -> the instant it means, or null. Offset-less = Toronto wall clock. */
export const parseWindowTime = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') return new Date(value);
  const s = String(value).trim();
  if (HAS_OFFSET.test(s) && /\d{2}:\d{2}/.test(s.slice(10))) return parseDbTime(s);   // carries its own zone
  const m = WALL.exec(s);
  if (!m) return null;
  const d = torontoWallToDate(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4] || 0), Number(m[5] || 0), Number(m[6] || 0));
  return isNaN(d.getTime()) ? null : d;
};

/** A window as Toronto wall-clock text (the same hours the hub, Trello and the apps show). */
export const fmtWindowDateTime = (value, opts) => {
  const d = parseWindowTime(value);
  return d ? d.toLocaleString(undefined, { ...(opts || {}), timeZone: WINDOW_TZ }) : '';
};
export const fmtWindowTime = (value, opts) => {
  const d = parseWindowTime(value);
  return d ? d.toLocaleTimeString(undefined, { ...(opts || { hour: 'numeric', minute: '2-digit' }), timeZone: WINDOW_TZ }) : '';
};

/** 'YYYY-MM-DD' of an instant's day in Toronto. */
export const torontoYMD = (d = new Date()) => {
  const x = d instanceof Date ? d : new Date(d);
  if (isNaN(x.getTime())) return '';
  const p = torontoParts(x.getTime());
  return p.y + '-' + String(p.mo).padStart(2, '0') + '-' + String(p.d).padStart(2, '0');
};

/**
 * The time-window picker's date + hour + minute (Toronto wall clock) -> the value the
 * window columns hold: 'YYYY-MM-DDTHH:MM:00', the hub's own time_windows.iso_at shape.
 * Built from the parts (no Date, no zone), so it cannot land on the wrong day.
 */
export const pickerToWindowValue = (ymd, hour, minute) => {
  const m = String(ymd || '').trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const h = parseInt(hour, 10), mi = parseInt(minute, 10);
  if (!m || isNaN(h) || isNaN(mi) || h < 0 || h > 23 || mi < 0 || mi > 59) return '';
  return `${m[1]}-${m[2]}-${m[3]}T${String(h).padStart(2, '0')}:${String(mi).padStart(2, '0')}:00`;
};

/** ...and back: a stored window as the date / hour / minute the picker shows (Toronto wall clock). */
export const windowValueToPickerParts = (value) => {
  if (value == null || value === '') return null;
  const s = String(value).trim();
  const m = WALL.exec(s);
  if (m) return { date: `${m[1]}-${m[2]}-${m[3]}`, hour: Number(m[4] || 0), minute: Number(m[5] || 0) };
  const d = parseWindowTime(s);   // a value carrying an offset
  if (!d) return null;
  const p = torontoParts(d.getTime());
  return { date: torontoYMD(d), hour: p.h, minute: p.mi };
};

export default { parseDbTime, minutesSince, fmtDbDateTime, fmtDbTime, fmtDbDate, localDateFromYMD, localYMD,
  WINDOW_TZ, torontoParts, torontoWallToDate, parseWindowTime, fmtWindowDateTime, fmtWindowTime, torontoYMD,
  pickerToWindowValue, windowValueToPickerParts };
